import React, { useState, useEffect } from 'react';

// SECURITY WARNING: This implementation uses client-side payment verification
// which is less secure than server-side verification. For production use,
// implement server-side verification using the provided server implementation
// in the server/ directory.
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import RazorpayLogo from '../components/RazorpayLogo';
import { addBookingToFirestore } from '../utils/firestoreBooking';
// Payment verification functions are now handled by razorpayOrders.js
import { sendBookingConfirmationEmail, sendAdminNotificationEmail } from '../utils/emailService';
import { debugBookingEmail } from '../utils/emailDebugHelper';
import { sendBookingConfirmationSMS, sendAdminSMSNotification } from '../utils/smsService';
import { validateBedSelection } from '../utils/bedAvailability';
import { createRazorpayOrder, verifyRazorpayPayment } from '../utils/razorpayOrders';
import { useLocation, useNavigate } from 'react-router-dom';
import './EnhancedBookingPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DormitoryLayout from '../booking-components/DormitoryLayout';
import BookingSummary from '../booking-components/BookingSummary';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCalendarAlt, FaBed, FaMapMarkerAlt, FaUsers, FaTools, FaWifi, FaCar, FaSnowflake, FaRegClock, FaToilet, FaLock } from 'react-icons/fa';
import { getRegularPlanDiscountPerNight } from '../utils/offers';

// Razorpay script loader
function loadRazorpayScript() {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    document.body.appendChild(script);
  });
}

const EnhancedBookingPage = () => {
  const location = useLocation();
  const packageInfo = location.state?.package;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  // Get booking data from URL parameters or session storage
  const urlParams = new URLSearchParams(window.location.search);
  
  // State declarations at the top to avoid initialization errors
  const [step, setStep] = useState(1); // 1: Bed Selection, 2: Personal Info, 3: Review & Payment
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showProcessingScreen, setShowProcessingScreen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('');
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Handle browser back/forward navigation to prevent getting stuck on payment page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // If user is on payment step or processing screen and tries to leave, show confirmation
      if ((step === 3 && showPaymentPopup) || showProcessingScreen) {
        e.preventDefault();
        e.returnValue = '';
        return 'Payment processing is in progress. Are you sure you want to leave?';
      }
    };
    
    const handlePopState = (e) => {
      // If user navigates back from payment step or processing screen, prevent navigation
      if (step === 3 || showProcessingScreen) {
        e.preventDefault();
        // Check if we have payment data in session storage
        const paymentInProgress = sessionStorage.getItem('paymentInProgress');
        if (paymentInProgress || showProcessingScreen) {
          // Show alert and prevent back navigation during processing
          alert('Please wait while we process your booking. Do not press back or refresh the page.');
          window.history.pushState(null, null, window.location.pathname);
          return false;
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [step, showPaymentPopup, showProcessingScreen, navigate]);
  
  // Ensure total is calculated correctly on mount
  useEffect(() => {
    setBookingData(prev => {
      // Only update if total is 0 or invalid
      if (!prev.total || prev.total <= 0) {
        const total = calculateInitialTotal(
          prev.beds,
          prev.nights,
          prev.pricePerBed,
          prev.package
        );
        return { ...prev, total };
      }
      return prev;
    });
  }, []);
  
  // Helper function to format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Parse display format (e.g., "Aug 15, 2023") and convert to YYYY-MM-DD
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    // Get local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    
    // Parse YYYY-MM-DD format from input
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) return dateString;
    
    // Create date using local components to avoid timezone issues
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (isNaN(date.getTime())) return dateString;
    
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  // Get today's date in YYYY-MM-DD format for min date
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Handle date changes
  const handleDateChange = (field, value) => {
    if (!value) return;
    
    const formattedDate = formatDisplayDate(value);
    
    setBookingData(prev => {
      const newData = { ...prev, [field]: formattedDate };
      
      // Auto-set checkout date based on package type when check-in date is selected
      if (field === 'start' && packageInfo?.plan) {
        const checkInDate = new Date(value);
        const checkOutDate = new Date(checkInDate);
        
        if (packageInfo.plan === 'Hourly Plan') {
          // Same day checkout for hourly plans
          newData.end = formattedDate; // Same as check-in date
          newData.nights = 1;
        } else if (packageInfo.plan === 'Weekly Plan') {
          // Weekly base = 6 nights (7 days stay)
          checkOutDate.setDate(checkInDate.getDate() + 6);
          newData.nights = 6;
          newData.end = formatDisplayDate(checkOutDate.toISOString().split('T')[0]);
        } else if (packageInfo.plan === 'Monthly Plan') {
          checkOutDate.setDate(checkInDate.getDate() + 30); // 30 days for monthly plan
          newData.nights = 30;
          newData.end = formatDisplayDate(checkOutDate.toISOString().split('T')[0]);
        }
        
        // Calculate total based on package type
        const total = calculateInitialTotal(
          newData.beds, 
          newData.nights, 
          newData.pricePerBed, 
          newData.package
        );
        newData.total = total;
        
        return newData;
      }
      
      // Recalculate nights if both dates are set
      if (newData.start && newData.end) {
        // Parse dates using the input format directly
        const startInputDate = formatDateForInput(newData.start);
        const endInputDate = formatDateForInput(newData.end);
        
        const startDate = new Date(startInputDate);
        const endDate = new Date(endInputDate);
        
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && endDate > startDate) {
          const nights = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
          newData.nights = nights;
          
          // Use the centralized calculation function for consistency
          const total = calculateInitialTotal(
            newData.beds, 
            nights, 
            newData.pricePerBed, 
            newData.package
          );
          newData.total = total;
        } else {
          newData.nights = 0;
        }
      }
      
      return newData;
    });
  };
  
  // Personal info state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  
  // Calculate initial total based on package or dormitory pricing
  const calculateInitialTotal = (beds, nights, pricePerBed, selectedPackage) => {
    // Ensure we have valid values to prevent 0rs glitch
    const validBeds = Math.max(1, parseInt(beds) || 1);
    const validNights = Math.max(1, parseInt(nights) || 1);
    const validPricePerBed = parseInt(pricePerBed) || 199;
    
    let basePrice = 0;
    
    if (selectedPackage && selectedPackage.price) {
      // Package booking - check package type
      if (selectedPackage.plan === 'Hourly Plan') {
        // Hourly packages: price is fixed per bed (no nights multiplication)
        basePrice = validBeds * selectedPackage.price;
      } else if (selectedPackage.plan === 'Weekly Plan') {
        // Weekly base (6 nights) + extras at ₹599 per bed per extra night
        const extraNights = Math.max(0, validNights - 6);
        basePrice = validBeds * (3499 + extraNights * 599);
      } else if (selectedPackage.plan === 'Monthly Plan') {
        // Monthly base (30 nights) + extras at ₹599 per bed per extra night
        const extraNights = Math.max(0, validNights - 30);
        basePrice = validBeds * (11999 + extraNights * 599);
      } else {
        // Regular Plan: price per bed per night
        const baseBeforeDiscount = validBeds * validNights * selectedPackage.price;
        const eligible = (selectedPackage.duration || '').includes('12-24');
        const discountPerNight = eligible ? getRegularPlanDiscountPerNight() : 0;
        const totalDiscount = discountPerNight * validBeds * validNights;
        basePrice = Math.max(0, baseBeforeDiscount - totalDiscount);
      }
    } else {
      // Dormitory booking - price is per bed per night
      basePrice = validBeds * validNights * validPricePerBed;
    }
    
    // Breakfast add-on removed
    
    const convenienceFee = Math.round(basePrice * 0.05);
    const total = basePrice + convenienceFee;
    
    // Ensure minimum total to prevent 0rs
    return Math.max(total, validPricePerBed);
  };

  // Booking data from URL params
  const initialBeds = parseInt(urlParams.get('beds')) || 1;
  const initialNights = Math.max(1, parseInt(urlParams.get('nights')) || 1);
  const initialPricePerBed = parseInt(urlParams.get('price')) || 199;
  const initialCheckInTime = urlParams.get('checkInTime') || '';
  const initialCheckOutTime = urlParams.get('checkOutTime') || '';
  
  const [bookingData, setBookingData] = useState({
    beds: initialBeds,
    start: urlParams.get('start') || '',
    end: urlParams.get('end') || '',
    nights: initialNights,
    pricePerBed: initialPricePerBed,
    location: 'Sindhi Colony, Hyderabad',
    package: packageInfo || null,
    checkInTime: initialCheckInTime,
    checkOutTime: initialCheckOutTime,
    total: calculateInitialTotal(initialBeds, initialNights, initialPricePerBed, packageInfo)
  });
  
  // Bed selection state
  const [selectedBeds, setSelectedBeds] = useState([]);
  const [bedCount, setBedCount] = useState(bookingData.beds || 1);
  
  // Calculate total - use the proper calculation function
  useEffect(() => {
    if (bookingData.beds > 0 && bookingData.nights > 0) {
      const total = calculateInitialTotal(
        bookingData.beds, 
        bookingData.nights, 
        bookingData.pricePerBed, 
        bookingData.package
      );
      console.log('Recalculating total:', { 
        beds: bookingData.beds, 
        nights: bookingData.nights, 
        pricePerBed: bookingData.pricePerBed, 
        package: bookingData.package, 
        calculatedTotal: total 
      });
      setBookingData(prev => ({ ...prev, total }));
    }
  }, [bookingData.beds, bookingData.nights, bookingData.pricePerBed, bookingData.package, bookingData.start, bookingData.end]);
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    const phoneTrimmed = formData.phone.trim();
    if (!phoneTrimmed) {
      errors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(phoneTrimmed)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    return errors;
  };
  
  const handleNext = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // If moving from step 1 to 2, check auth
    if (step === 1 && !currentUser) {
      setShowAuthModal(true);
      return;
    }
    if (step === 2) {
      const errors = validateForm();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        toast.error('Please fix the errors in the form');
        return;
      }
      setFormErrors({});
    }
    
    if (step < 3) {
      setStep(step + 1);
    }
  };
  
  const handleBack = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (step > 1) {
      setStep(step - 1);
    }
  };
  
  const handleBedCountChange = (newCount) => {
    setBedCount(newCount);
    setBookingData(prev => ({ ...prev, beds: newCount }));
    // Clear selected beds if count is reduced
    if (newCount < selectedBeds.length) {
      setSelectedBeds(selectedBeds.slice(0, newCount));
    }
  };

  // Breakfast add-on removed

  const handleBedsSelected = (beds) => {
    setSelectedBeds(beds);
    setStep(2);
  };
  
  const handleEditBeds = () => {
    setStep(1); // Go back to bed selection
  };
  
  // Async function to create booking after payment verification
  async function createBookingAfterPayment(response, verificationResult) {
    const bookingId = `SKY${Date.now().toString(36).substr(2, 9).toUpperCase()}`;
    console.log('Creating booking with ID:', bookingId);
    
    try {
      // Use actual form data - validation ensures these fields are populated
      const guestData = {
        firstName: formData.firstName,
        lastName: formData.lastName, 
        email: formData.email,
        phone: formData.phone,
        specialRequests: formData.specialRequests || ''
      };
      
      const stayData = {
        start: bookingData.start || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        end: bookingData.end || new Date(Date.now() + 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        nights: bookingData.nights || 1,
        beds: bookingData.beds || 1,
        total: bookingData.total || 1000,
        pricePerBed: bookingData.pricePerBed || 600,
        location: bookingData.location || 'Sindhi Colony, Hyderabad'
      };
      
      const bedData = selectedBeds.length > 0 ? selectedBeds : [{ id: 'A1', position: 'Window', roomId: '1' }];
      
      // Create comprehensive booking data
      const bookingDetails = {
        id: bookingId,
        userId: currentUser?.uid || 'test_user',
        userName: currentUser?.displayName || currentUser?.name || currentUser?.email || 'Guest User',
        guestName: `${guestData.firstName} ${guestData.lastName}`,
        guestEmail: guestData.email,
        checkIn: stayData.start,
        checkOut: stayData.end,
        guestPhone: guestData.phone,
        bedType: 'Mixed Dormitory',
        selectedBeds: bedData.map(bed => ({
          ...bed,
          roomId: bed.roomId || 'Dormitory',
          position: bed.position || 'N/A'
        })),
        beds: stayData.beds,
        nights: stayData.nights,
        pricePerBed: stayData.pricePerBed,
        amount: stayData.total,
        total: stayData.total,
        status: 'Confirmed',
        payment: 'paid',
        paymentDetails: {
          paymentId: response.razorpay_payment_id || 'test_payment_' + Date.now(),
          orderId: response.razorpay_order_id || 'test_order_' + Date.now(),
          signature: response.razorpay_signature || 'test_signature',
          verified: true,
          verificationStatus: verificationResult?.status || 'verified'
        },
        extraCharges: [],
        createdAt: new Date().toISOString(),
        paymentMethod: 'razorpay',
        bookingData: stayData,
        formData: guestData,
        selectedPackage: bookingData.package || null
      };

      console.log('Booking details prepared:', bookingDetails);

      // Try to save to Firebase but don't let it block email sending
      let firebaseSaved = false;
      try {
        console.log('Attempting to save booking to Firebase...');
        const firestoreDocId = await addBookingToFirestore(bookingDetails);
        console.log('Booking saved to Firebase successfully with document ID:', firestoreDocId);
        firebaseSaved = true;
      } catch (firebaseError) {
        console.error('Firebase save failed, but continuing with email:', firebaseError);
        firebaseSaved = false;
      }
      
      // Send email and SMS notifications regardless of Firebase status
      let emailResult = { success: false };
      let smsResult = { success: false };
      let adminEmailResult = { success: false };
      let adminSMSResult = { success: false };
      
      try {
        console.log('📧 Attempting to send booking confirmation email...');
        console.log('📧 Email details:', {
          guestEmail: bookingDetails.guestEmail,
          guestName: bookingDetails.guestName,
          bookingId: bookingDetails.id
        });
        console.log('📧 Full booking details for email:', bookingDetails);
        
        // Send booking confirmation email directly
        emailResult = await sendBookingConfirmationEmail(bookingDetails);
        console.log('📧 Email service response:', emailResult);
        
        // Send SMS notification
        console.log('📱 Attempting to send booking confirmation SMS...');
        smsResult = await sendBookingConfirmationSMS(bookingDetails);
        console.log('📱 SMS service response:', smsResult);
        
        // Log detailed debugging information for console only
        let debugInfo = `📧 EMAIL DEBUG INFO:\n`;
        debugInfo += `- Guest Email: ${bookingDetails.guestEmail || 'MISSING'}\n`;
        debugInfo += `- EmailJS Service ID: ${process.env.REACT_APP_EMAILJS_SERVICE_ID || 'NOT SET'}\n`;
        debugInfo += `- EmailJS Template ID: ${process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'NOT SET'}\n`;
        debugInfo += `- EmailJS Public Key: ${process.env.REACT_APP_EMAILJS_PUBLIC_KEY ? 'SET' : 'NOT SET'}\n`;
        debugInfo += `- Email Result: ${emailResult.success ? 'SUCCESS' : 'FAILED'}\n`;
        if (!emailResult.success) {
          debugInfo += `- Email Error: ${emailResult.error}\n`;
        }
        
        debugInfo += `\n📱 SMS DEBUG INFO:\n`;
        debugInfo += `- Guest Phone: ${bookingDetails.guestPhone || 'MISSING'}\n`;
        debugInfo += `- SMS Result: ${smsResult.success ? 'SUCCESS' : 'FAILED'}\n`;
        if (!smsResult.success) {
          debugInfo += `- SMS Error: ${smsResult.error}\n`;
        }
        
        // Log notification results to console (no popup)
        console.log('📧📱 Notification Results:', debugInfo);
        if (emailResult.success && smsResult.success) {
          console.log('✅ Booking confirmed! Both email and SMS sent successfully!');
        } else if (emailResult.success) {
          console.log('✅ Booking confirmed! Email sent successfully, SMS failed.');
        } else if (smsResult.success) {
          console.log('✅ Booking confirmed! SMS sent successfully, email failed.');
        } else {
          console.log('⚠️ Booking confirmed but both email and SMS failed!');
        }
        
        // Send admin notifications
        console.log('📧 Sending admin notification email...');
        adminEmailResult = await sendAdminNotificationEmail(bookingDetails);
        if (adminEmailResult.success) {
          console.log('✅ Admin notification email sent successfully');
        } else {
          console.log('❌ Admin notification email failed:', adminEmailResult.error);
        }
        
        console.log('📱 Sending admin notification SMS...');
        adminSMSResult = await sendAdminSMSNotification(bookingDetails);
        if (adminSMSResult.success) {
          console.log('✅ Admin notification SMS sent successfully');
        } else {
          console.log('❌ Admin notification SMS failed:', adminSMSResult.error);
        }
        
      } catch (notificationError) {
        console.error('❌ Notification error:', notificationError);
        console.log('⚠️ Booking confirmed but notifications failed. Please contact us at booking@skylinebackpackers.in');
        // Don't fail the booking process if notifications fail
      }
      
      // Navigate to confirmation after successful save
      try {
        // Hide processing screen before navigation
        setShowProcessingScreen(false);
        navigate('/booking-confirmation', {
          state: {
            formData: guestData,
            bookingData: stayData,
            selectedBeds: bedData,
            paymentMethod: 'razorpay',
            bookingId: bookingDetails.id,
            paymentVerified: true,
            bookingDetails,
            firebaseSaved: firebaseSaved,
            emailSent: emailResult.success,
            adminNotified: adminEmailResult.success
          }
        });
      } catch (navError) {
        console.error('Navigation to confirmation failed, using fallback');
        // Store booking data in sessionStorage for confirmation page
        sessionStorage.setItem('bookingConfirmation', JSON.stringify({
          formData: guestData,
          bookingData: stayData,
          selectedBeds: bedData,
          paymentMethod: 'razorpay',
          bookingId: bookingDetails.id,
          paymentVerified: true,
          bookingDetails,
          firebaseSaved: firebaseSaved
        }));
        // Fallback navigation
        window.location.href = '/booking-confirmation';
      }
      
    } catch (error) {
      console.error('Error in createBookingAfterPayment:', error);
      
      // Try to save to localStorage as backup
      const backupBookingData = {
        id: bookingId,
        userId: currentUser.uid,
        guestName: formData.firstName + ' ' + formData.lastName,
        guestEmail: formData.email,
        guestPhone: formData.phone,
        checkIn: bookingData.start,
        checkOut: bookingData.end,
        beds: bookingData.beds,
        nights: bookingData.nights,
        amount: bookingData.total,
        total: bookingData.total,
        status: 'Confirmed',
        payment: 'paid',
        paymentMethod: paymentMethod || 'razorpay',
        createdAt: new Date().toISOString(),
        formData: formData,
        bookingData: {
          ...bookingData
        },
        selectedBeds: selectedBeds,
        paymentDetails: {
          paymentId: response.razorpay_payment_id || 'test_payment_' + Date.now(),
          verified: true
        }
      };
      
      // Save to localStorage as backup
      try {
        const existingBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
        existingBookings.push(backupBookingData);
        localStorage.setItem('userBookings', JSON.stringify(existingBookings));
        console.log('Booking saved to localStorage as backup');
      } catch (localStorageError) {
        console.error('Failed to save to localStorage:', localStorageError);
      }
      
      // Retry Firebase save once more
      try {
        console.log('Retrying Firebase save...');
        await addBookingToFirestore(backupBookingData);
        console.log('Retry successful - booking saved to Firebase');
        
        // Navigate to confirmation with success
        const successData = {
          formData,
          bookingData: {
            ...bookingData
          },
          selectedBeds,
          paymentMethod,
          bookingId,
          paymentVerified: true,
          bookingDetails: backupBookingData,
          firebaseSaved: true
        };
        
        try {
          navigate('/booking-confirmation', { state: successData });
        } catch (navError) {
          sessionStorage.setItem('bookingConfirmation', JSON.stringify(successData));
          window.location.href = '/booking-confirmation';
        }
        return;
        
      } catch (retryError) {
        console.error('Retry failed:', retryError);
      }
      
      // Still navigate to confirmation but with error flag
      const errorBookingData = {
        formData,
        bookingData: {
          ...bookingData
        },
        selectedBeds,
        paymentMethod: paymentMethod || 'razorpay',
        bookingId,
        paymentVerified: true,
        saveError: true,
        bookingDetails: backupBookingData
      };

      try {
        navigate('/booking-confirmation', { state: errorBookingData });
      } catch (navError) {
        console.error('Navigation failed in error handler, using fallback');
        sessionStorage.setItem('bookingConfirmation', JSON.stringify(errorBookingData));
        window.location.href = '/booking-confirmation';
      }
    }
  }

  // Function to handle payment failure
  function handlePaymentFailure(error) {
    console.error('Payment failed:', error);
    // Clear payment progress flag
    sessionStorage.removeItem('paymentInProgress');
    
    // Create URL parameters for payment failure data
    const failureParams = new URLSearchParams({
      error: error.error || 'Payment failed',
      code: error.code || 'UNKNOWN_ERROR',
      beds: bookingData.beds.toString(),
      nights: bookingData.nights.toString(),
      total: bookingData.total.toString(),
      checkIn: bookingData.start || '',
      checkOut: bookingData.end || '',
      timestamp: Date.now().toString()
    });
    
    // Store error data in sessionStorage as backup
    sessionStorage.setItem('paymentError', JSON.stringify({
      formData,
      bookingData,
      selectedBeds,
      paymentMethod,
      error: error,
      timestamp: Date.now()
    }));
    
    // Ensure we always navigate to payment failure page with multiple fallbacks
    try {
      navigate('/payment-failure', {
        state: {
          formData,
          bookingData,
          selectedBeds,
          paymentMethod,
          error: error
        }
      });
    } catch (navError) {
      console.error('React navigation failed, using fallback redirect');
      // Fallback 1: Try with URL parameters
      try {
        window.location.replace(`/payment-failure?${failureParams.toString()}`);
      } catch (replaceError) {
        console.error('Replace failed, using href fallback');
        // Fallback 2: Direct window location change
        window.location.href = `/payment-failure?${failureParams.toString()}`;
      }
    }
  }

  const handlePayment = async () => { 
    console.log('🚀 PAYMENT STARTED');
    
    // Validate form data before payment to ensure we have real user details
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please complete all required fields before payment');
      return;
    }
    
    // Ensure we have actual user data, not fallbacks
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields to proceed with payment');
      return;
    }
    
    console.log('✅ Form validation passed with actual user data:', {
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      phone: formData.phone
    });
    
    // Validate bed selection before payment
    const selectedBedIds = selectedBeds.map(bed => bed.id || bed);
    const validation = await validateBedSelection(
      selectedBedIds, 
      formatDateForInput(bookingData.start), 
      formatDateForInput(bookingData.end)
    );
    
    if (!validation.valid) {
      toast.error(validation.message);
      console.error('❌ Bed validation failed:', validation);
      return;
    }
    
    console.log('✅ Bed validation passed');
    
    const total = bookingData.total || 1000;
    console.log('💰 Using total:', total);
    
    setShowPaymentPopup(true);
    setLoading(true);
    
    // Add timeout protection for payment initialization
    const paymentTimeout = setTimeout(() => {
      setLoading(false);
      setShowPaymentPopup(false);
      // Remove payment progress flag
      sessionStorage.removeItem('paymentInProgress');
      handlePaymentFailure({
        success: false,
        error: 'Payment initialization timed out. Please try again.',
        code: 'PAYMENT_TIMEOUT'
      });
    }, 15000); // 15 second timeout
    
    try {
      console.log('📦 Loading Razorpay script...');
      await loadRazorpayScript();
      console.log('✅ Razorpay script loaded successfully');
      
      clearTimeout(paymentTimeout);
      setLoading(false);
      setShowPaymentPopup(false);

      // Create Razorpay order first (prevents automatic refunds)
      console.log('🏗️ Creating Razorpay order...');
      const order = await createRazorpayOrder(total, 'INR', `booking_${Date.now()}`);
      console.log('✅ Order created:', order);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount, // Amount from order
        currency: order.currency,
        name: 'Skyline Backpackers',
        description: 'Bed Booking Payment',
        handler: async function (response) {
          console.log('✅ Payment successful:', response);
          console.log('✅ Payment response details:', {
            payment_id: response.razorpay_payment_id,
            order_id: response.razorpay_order_id,
            signature: response.razorpay_signature ? 'Present' : 'Missing'
          });
          
          // Show processing screen immediately since payment succeeded
          setShowPaymentPopup(false);
          setShowProcessingScreen(true);
          
          try {
            // Verify payment with order details
            const verificationResult = await verifyRazorpayPayment(response);
            console.log('✅ Payment verified:', verificationResult);
            
            // Create booking after verification
            createBookingAfterPayment(response, verificationResult);
          } catch (verifyError) {
            console.error('❌ Payment verification failed but payment succeeded:', verifyError);
            
            // Since payment succeeded (we're in the success handler), create booking anyway
            // with a fallback verification result
            const fallbackVerification = {
              verified: true,
              order_id: response.razorpay_order_id || 'mock_order',
              payment_id: response.razorpay_payment_id,
              status: 'captured',
              verified_at: new Date().toISOString(),
              is_mock_order: !response.razorpay_order_id,
              fallback_verification: true
            };
            
            console.log('🔄 Using fallback verification for successful payment:', fallbackVerification);
            
            // Create booking with fallback verification
            try {
              createBookingAfterPayment(response, fallbackVerification);
            } catch (bookingError) {
              console.error('❌ Booking creation failed even with fallback:', bookingError);
              handlePaymentFailure({
                success: false,
                error: 'Booking creation failed after successful payment. Please contact support.',
                code: 'BOOKING_CREATION_FAILED'
              });
            }
          }
        },
        prefill: {
          name: `${formData.firstName} ${formData.lastName}` || 'Guest',
          email: formData.email || currentUser?.email || 'guest@skylinebackpackers.in',
          contact: formData.phone || '0000000000'
        },
        theme: {
          color: '#3399cc'
        },
        modal: {
          ondismiss: function() {
            console.log('Payment modal dismissed');
            handlePaymentFailure({
              success: false,
              error: 'Payment cancelled by user',
              code: 'USER_CANCELLED'
            });
          }
        }
      };
      // Check if Razorpay key is configured
      if (!process.env.REACT_APP_RAZORPAY_KEY_ID || process.env.REACT_APP_RAZORPAY_KEY_ID === 'rzp_test_your_key_id') {
        console.error('Razorpay key not configured properly');
        toast.error('Payment gateway not configured. Please contact support.');
        handlePaymentFailure({
          success: false,
          error: 'Payment gateway not configured. Please contact support.',
          code: 'RAZORPAY_KEY_MISSING'
        });
        return;
      }

      console.log('🔑 Razorpay Key:', process.env.REACT_APP_RAZORPAY_KEY_ID);

      if (!window.Razorpay) {
        console.error('❌ Razorpay not available');
        throw new Error('Razorpay script not loaded');
      }

      const rzp = new window.Razorpay(options);
      console.log('🚀 Opening Razorpay...');
      rzp.open();
    } catch (error) {
      clearTimeout(paymentTimeout); // Clear timeout on error
      setLoading(false);
      setShowPaymentPopup(false);
      console.error('❌ Payment initiation failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      // Show user-friendly error message
      toast.error(`Payment failed: ${error.message || 'Unknown error'}`);
      
      handlePaymentFailure({
        success: false,
        error: error.message || 'Failed to initiate payment',
        code: 'PAYMENT_INIT_FAILED'
      });
    }
  };

  return (
    <>
      <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} />
      <Header />
      <div className="enhanced-booking-page">
        <div className="booking-container">
          <div className="booking-progress">
            <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">Select Beds</div>
            </div>
            <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-label">Your Info</div>
            </div>
            <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <div className="step-label">Review & Pay</div>
            </div>
          </div>
          
          {/* Step 1: Bed Selection */}
          {step === 1 && (
            <div className="booking-step">
              <div className="step-header">
                <h2>Select Your Beds</h2>
                <p>Choose the number of beds and select your preferred beds</p>
              </div>
              
              {/* Bed Count Selector */}
              <div className="selection-section">
                <h3>Number of Beds</h3>
                <div className="bed-counter-simple">
                  <button 
                    className="counter-btn-simple minus" 
                    onClick={() => handleBedCountChange(Math.max(1, bedCount - 1))}
                    disabled={bedCount <= 1}
                  >
                    −
                  </button>
                  <div className="counter-display-simple">
                    <FaBed className="bed-icon-simple" />
                    <span className="bed-count-simple">{bedCount}</span>
                  </div>
                  <button 
                    className="counter-btn-simple plus" 
                    onClick={() => handleBedCountChange(Math.min(6, bedCount + 1))}
                    disabled={bedCount >= 6}
                  >
                    +
                  </button>
                </div>
                <div className="selection-info">
                  <span>Selected: {bedCount} bed{bedCount > 1 ? 's' : ''}</span>
                  <span>Beds to select: {Math.max(0, bedCount - selectedBeds.length)} remaining</span>
                </div>
              </div>

              

              {/* Dormitory Layout for Bed Selection */}
              {bedCount > 0 && (
                <DormitoryLayout
                  bedsToSelect={bedCount}
                  onConfirm={handleBedsSelected}
                  onCancel={() => setStep(1)}
                  selectedBeds={selectedBeds}
                  checkInDate={bookingData.start}
                  checkOutDate={bookingData.end}
                  onDateChange={handleDateChange}
                  packageInfo={packageInfo}
                />
              )}
          </div>
        )}
        
        {/* Step 2: Personal Information */}
        {step === 2 && (
          <div className="booking-step">
            <div className="step-header">
              <h2>Guest Information</h2>
              <p>Please provide your details for the booking</p>
            </div>
            
            <div className="form-section">
                <div className="form-row">
                  <div className="form-col">
                    <label htmlFor="firstName">First Name *</label>
                    <input 
                      type="text" 
                      id="firstName"
                      placeholder="Your first name" 
                      required 
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    />
                    {formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}
                  </div>
                  <div className="form-col">
                    <label htmlFor="lastName">Last Name *</label>
                    <input 
                      type="text" 
                      id="lastName"
                      placeholder="Your last name" 
                      required 
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    />
                    {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-col">
                    <label htmlFor="email">Email Address *</label>
                    <input 
                      type="email" 
                      id="email"
                      placeholder="your@email.com" 
                      required 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                    {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                  </div>
                  <div className="form-col">
                    <label htmlFor="phone">Phone Number *</label>
                    <input 
                      type="tel" 
                      id="phone"
                      placeholder="10-digit mobile number" 
                      required 
                      maxLength={10}
                      value={formData.phone}
                      onChange={(e) => setFormData({
                        ...formData,
                        phone: e.target.value.replace(/\D/g, '').trim()
                      })}
                    />
                    {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-col full-width">
                    <label htmlFor="specialRequests">Special Requests</label>
                    <textarea 
                      id="specialRequests"
                      placeholder="Any special requests or preferences..."
                      value={formData.specialRequests}
                      onChange={(e) => setFormData({...formData, specialRequests: e.target.value})}
                    ></textarea>
                  </div>
                </div>
              </div>
              
              <div className="step-actions">
                <button className="btn btn-secondary" onClick={handleBack}>
                  Back
                </button>
                <button className="btn btn-primary" onClick={handleNext}>
                  Continue to Review
                </button>
              </div>
            </div>
          )}
          
          {/* Step 3: Review & Payment */}
          {step === 3 && (
            <div className="booking-step review-step">
              <div className="step-header">
                <h2>Review Your Booking</h2>
                <p>Please review all details before payment</p>
              </div>
              
              <div className="review-content">
                <div className="personal-info-review">
                  <h3>Guest Information</h3>
                  <div className="info-row">
                    <span>Name:</span>
                    <span>{formData.firstName} {formData.lastName}</span>
                  </div>
                  <div className="info-row">
                    <span>Email:</span>
                    <span>{formData.email}</span>
                  </div>
                  <div className="info-row">
                    <span>Phone:</span>
                    <span>{formData.phone}</span>
                  </div>
                  {formData.specialRequests && (
                    <div className="info-row">
                      <span>Special Requests:</span>
                      <span>{formData.specialRequests}</span>
                    </div>
                  )}
                  <button className="edit-info-btn" onClick={() => setStep(2)}>
                    Edit Information
                  </button>

                  {/* Contact Us Section */}
                  <div className="contact-us-section">
                    <h3>Contact Us</h3>
                    <div className="info-row">
                      <span><i className="contact-icon fa fa-phone" aria-hidden="true"></i> Phone:</span>
                      <span><a href="tel:+919032797797">+91 90327 97797</a></span>
                    </div>
                    <div className="info-row">
                      <span><i className="contact-icon fa fa-envelope" aria-hidden="true"></i> Email:</span>
                      <span><a href="mailto:booking@skylinebackpackers.in">booking@skylinebackpackers.in</a></span>
                    </div>
                  </div>
                </div>
                
                <BookingSummary 
                  bookingData={{...bookingData, paymentMethod}}
                  selectedBeds={selectedBeds}
                  onEditBeds={handleEditBeds}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  termsChecked={termsChecked}
                  setTermsChecked={setTermsChecked}
                />
              </div>
              
              <div className="step-actions">
                <button className="btn btn-secondary" onClick={handleBack}>
                  Back
                </button>
                <button className="btn btn-primary" onClick={handlePayment} disabled={!paymentMethod || loading || !termsChecked || !(bookingData.total > 0)}>
                  {loading ? 'Processing Payment...' : `Pay ₹${calculateInitialTotal(bookingData.beds, bookingData.nights, bookingData.pricePerBed, bookingData.package) || 0}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Payment Popup */}
      {showPaymentPopup && (
        <div className="payment-popup-overlay">
          <div className="payment-popup">
            <div className="payment-popup-content">
              <h3>Redirecting to Payment Gateway</h3>
              <div className="dots-loader">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <p>You will now be redirected to Razorpay for payment processing.</p>
              <div className="razorpay-logo">
                <RazorpayLogo style={{ maxWidth: 180, width: '100%', height: 'auto', margin: '0 auto', display: 'block' }} />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Processing Screen */}
      {showProcessingScreen && (
        <div className="processing-screen-overlay">
          <div className="processing-screen">
            <div className="processing-content">
              <div className="processing-icon">
                <div className="spinner"></div>
              </div>
              <h2>Processing Your Booking</h2>
              <p className="processing-message">
                ⚠️ <strong>Please do not press back or refresh this page</strong>
              </p>
              <p>We are confirming your payment and creating your booking...</p>
              <div className="processing-steps">
                <div className="step active">✓ Payment Received</div>
                <div className="step active">⏳ Saving Booking Details</div>
                <div className="step">📧 Sending Confirmation Email</div>
                <div className="step">🎉 Redirecting to Confirmation</div>
              </div>
              <p className="processing-note">This may take a few moments. Thank you for your patience!</p>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
      <ToastContainer position="bottom-center" autoClose={3000} />
    </>
  );
};

export default EnhancedBookingPage;
